import { useEffect, useState } from 'react'
import { Image as CrystallizeImage } from '@crystallize/react-image'
import { useQuery } from '@tanstack/react-query'
import Image from 'next/image'

import { useBasket } from '@/app/[locale]/(basket)/index'
import { BasketItem } from '@/app/[locale]/(basket)/basket-item'
import { Coupon, CouponStatus, CouponStatusType } from '@/app/[locale]/(basket)/coupon'

import { useDebounce } from '@/hooks/use-debounce'
import type { VoucherResponse } from '@/service-api/types.generated'
import { useDeliveryAddress } from '@/stores/useAddressStore'
import { useCoupon, useSetCoupon } from '@/stores/useGlobalStore'
import { isAddressInDeliveryArea, isBagProduct, isFreightProduct } from '@/lib/freight'
import { serviceApi } from '@/lib/service-api'
import { useTranslations } from 'next-intl'

const DEBOUNCE_TIMEOUT_IN_MS = 500
const FIVE_MINUTES_IN_MS = 1000 * 60 * 5

export const BasketItems = () => {
  const [couponStatus, setCouponStatus] = useState<CouponStatusType>(CouponStatus.NOT_CHECKED)
  const { cart, actions, isCompany } = useBasket()
  const t = useTranslations()
  const deliveryAddress = useDeliveryAddress()

  const coupon = useCoupon()
  const setCoupon = useSetCoupon()

  const debouncedValue = useDebounce<string>(coupon || '', DEBOUNCE_TIMEOUT_IN_MS)

  const hasBags = cart.some((item) => isBagProduct(item))
  const isValidAddress = deliveryAddress
    ? isAddressInDeliveryArea(deliveryAddress.kommunenummer, deliveryAddress.postnummer)
    : false
  const shouldShowFreightPlaceholder = hasBags && (!deliveryAddress || !isValidAddress)

  const freightItem = cart.find((item) => isFreightProduct(item))

  const { data, isLoading } = useQuery({
    queryKey: ['coupon', coupon],
    queryFn: async () => {
      const response = await serviceApi({
        query: `
        query getVoucher($code: String!) {
          voucher(code: $code) {
            isValid,
            voucher {
              code
              discountAmount
              discountPercent
            }
          }
        }
      `,
        variables: { code: coupon },
      })

      return response?.data as { voucher: VoucherResponse }
    },
    staleTime: FIVE_MINUTES_IN_MS,
    cacheTime: FIVE_MINUTES_IN_MS,
    enabled: !!coupon,
  })

  useEffect(() => {
    // Reset status when coupon is empty
    if (!coupon || coupon.trim() === '') {
      setCouponStatus(CouponStatus.NOT_CHECKED)
      actions.removeVoucherCode()
      return
    }

    if (isLoading || !data) return

    if (data?.voucher?.isValid) {
      setCouponStatus(CouponStatus.VALID)
      actions.addVoucherCode(coupon)
      return
    }

    actions.removeVoucherCode()
    setCouponStatus(CouponStatus.INVALID)
  }, [coupon, data, isLoading])

  const FreightPlaceholder = () => {
    if (!freightItem) return null

    return (
      <div className="w-full bg-white p-2.5">
        <div className="flex w-full gap-5">
          <div className="flex flex-col">
            <CrystallizeImage
              url={freightItem.images?.[0]?.url}
              className="flex size-[20px] rounded border border-gray-200"
            />
            <div className="w-[70px]">
              {freightItem.productImage ? (
                <Image alt="" src={freightItem.productImage} width={60} height={60} layout="responsive" />
              ) : (
                <div className="flex h-[60px] w-[60px] items-center justify-center bg-gray-100">
                  <span className="text-xs text-gray-600">{t('No_image')}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-grow flex-col items-start justify-start gap-2 self-stretch">
            <div className="font-bold">{t('Freight')}</div>
            <div className="text-sm italic text-gray-600">{t('Freight_calculated_when_address_selected')}</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {!isCompany && shouldShowFreightPlaceholder && <FreightPlaceholder />}
      {cart
        ?.filter((item) => {
          if (isCompany && isFreightProduct(item)) {
            return false
          }

          if (isFreightProduct(item) && (!deliveryAddress || !isValidAddress)) {
            return false
          }

          return true
        })
        .sort((a, b) => {
          // Sort freight items first
          const aIsFreight = isFreightProduct(a)
          const bIsFreight = isFreightProduct(b)

          if (aIsFreight && !bIsFreight) return -1
          if (!aIsFreight && bIsFreight) return 1
          return 0
        })
        .map((item) => (
          <BasketItem
            key={`${item.sku || ''}-${item.path || ''}`}
            item={item}
            discount={
              coupon && couponStatus === CouponStatus.VALID ? (data?.voucher?.voucher?.discountPercent ?? 0) : 0
            }
          />
        ))}
      {!isCompany && (
        <Coupon
          coupon={debouncedValue}
          setCoupon={setCoupon}
          couponStatus={couponStatus}
          isValidating={!!coupon && isLoading}
          startExpanded={!!coupon}
        />
      )}
    </>
  )
}
